<template>
  <router-link 
    v-if="avatarInfo"
    class="avatar" 
    :class="!props.canRoute ? 'not-route' : ''"
    :to="avatarRouter"
    @click.prevent="onPreview"
  >
    <div class="avatar-image" :style="{
      width: `${size}px`,
      height:`${size}px`,
    }">
      <img 
        v-if="avatarInfo.avatarWidget"
        class="avatarWidget"
        :src="$formatImg(avatarInfo.avatarWidget)"
        :style="{
          width: `${size}px`,
          height:`${size}px`,
          transform: `scale(${avatarInfo.avatarResizeRate})`,
        }"
      />
      <!-- 头像 -->
      <ObImg
        :formatWidth="(size * 2)"
        :style="{
          width: `${size}px`,
          height:`${size}px`,
        }"
        :src="avatarImage"
        :key="avatarImage"
        :round="true"
        :lazy="true"
        :errorImg="defaultAvatar"
      />
      <!-- 标志 -->
      <ObImg
        v-if="(avatarInfo.icon && props.icon)"
        class="avatar-icon"
        formatWidth="136"
        :round="true"
        :lazy="true"
        :style="{
          ...iconStyle,
          width: `${iconSize}px`,
          height:`${iconSize}px`,
        }"
        :src="avatarInfo.icon" 
        :key="avatarInfo.icon"
      />
    </div>
    <!-- 昵称 -->
    <slot></slot>
  </router-link>
</template>

<script setup>
import { ref, watch, computed, getCurrentInstance, nextTick } from 'vue';
import { useSystemStore } from '@/stores/system'
import { storeToRefs } from 'pinia'

import defaultAvatar from '@/assets/photo/defaultAvatar.png'
import { useRoute, useRouter } from 'vue-router'
// import { useUserStore } from '@/stores/user'

const route = useRoute()
const router = useRouter()
const { proxy } = getCurrentInstance()

const systemStore = useSystemStore()
// const userStore = useUserStore() 
const { isApp, isOneplus, versionCode } = storeToRefs(systemStore)
// const { isBetaUser } = storeToRefs(userStore)

/**
 * @avatarInfo {icon:图标， avatar:头像， id:用户id}
 * @size 头像尺寸
 */  
const props = defineProps({
  avatarInfo: {
    type: Object,
    default: null
  },
  size: {
    type: [Number, String],
    default: 32
  },
  iconSize: {
    type: [Number, String],
    default: 11
  },
  icon: {
    type: Boolean,
    default: true
  },
  canRoute: {
    type: Boolean,
    default: true
  },
  canView: {
    type: Boolean,
    default: true
  },
  entryPosition: {
    type: String,
    default: ''
  },
  callBack: {
    type: Function,
    default: () => {}
  }
})

const iconBorderSize = computed(() => {
  const iconSize = Number(props.iconSize)
  if (iconSize > 80) {
    return 2
  } else if (iconSize > 48) {
    return 1.5
  } else if (iconSize > 32) {
    return 1
  } else if (iconSize > 24) {
    return 0.8
  } else {
    return 0.66
  }
})

// 个人详情路由
const avatarRouter = computed(() => {
  return (isApp.value && route.name === 'abroadList') || isOneplus.value || !props.canRoute ? {} : {
    name: 'user',
    params: {
      id: props.avatarInfo.id
    }
  }
})

const avatarImage = computed(() => {
  return props.avatarInfo.avatar
})


// 头像尺寸伪类
const iconStyle = computed(() => {
  if (props.avatarInfo.icon) {
    return {
      '--icon-ml': `-${props.iconSize}px`, // icon左位移
      '--icon-mt': `-${props.iconSize}px`,  // icon上位移
      '--icon-size': iconBorderSize.value < 1 ? `calc((${props.iconSize}px - (${props.iconSize}px * ${iconBorderSize.value})) + ${props.iconSize}px)` : `${props.iconSize}px`,  // icon尺寸
      '--icon-iconBorderSize.value': iconBorderSize.value < 1 ? '1px' : `${iconBorderSize.value}px`,  // icon边框
      '--icon-transform': iconBorderSize.value < 1 ? iconBorderSize.value : 1,  // icon缩放尺寸
      '--icon-translate': iconBorderSize.value < 1 ? 0 : `-${iconBorderSize.value}px` // icon定位距离
    }
  }
  return {}
})

const onPreview = (e) => {
  if (props.callBack) {
    props.callBack()
  }
  // 无跳转、无预览功能
  if (!props.canRoute || !props.canView) {
    e.preventDefault();
    return
  } 

  proxy.$ga('user_homepage_entry', {
    "screen_name": route.name,
    "userId": props.avatarInfo.id,
    "entry_position": props.entryPosition ? props.entryPosition : route.name
  })
  // 仅预览
  if (!props.canRoute) {
    // previewStore.showPreviewImgs([props.avatarInfo.avatar], 0) 
    return
  }
  // 跳转
  if (isOneplus.value && versionCode.value < 41600) {
    router.push({
      name: 'user',
      params: {
        id: props.avatarInfo.id
      }
    })
    return
  }
  if ((isApp.value && route.name === 'abroadList') || isOneplus.value) {
    proxy.$openNewWebview(`${location.origin}/user/${props.avatarInfo.id}`, {
      name: 'user',
      params: {
        id: props.avatarInfo.id
      }
    })
  }
}


</script>
<style lang="postcss" scoped>
  .not-route {
    cursor: default;
    pointer-events: none;
  }
  .avatar {
    flex: 1;
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: flex-start;
    .avatarWidget {
      position: absolute;
      width: 132% !important;
      height: 132% !important;
      top: -16%;
      left: -16%;
      z-index: 10;
      background-color: transparent;
      transform-origin: 50%;
      object-fit: cover;
    }
    .el-image {
      border-radius: 50%;
    }
    &-image {
      position: relative;
    }
    &-icon {
      position: absolute;
      overflow: unset;
      right: 2px;
      bottom: 2px;
      z-index: 20;
      :deep(img) {
        border-radius: 50%;
        border: 1px solid #FFF;
      }
      &::before {
        content: ' ';
        position: absolute;
        top: var(--icon-translate);
        left: var(--icon-translate);
        width: var(--icon-size);
        height: var(--icon-size);
        transform-origin: 0 0;
        transform: scale(var(--icon-transform));
        /* border: var(--icon-border) solid #FFF; */
        border-radius: 50%;
        z-index: 10;
      }
    }
    
  }
</style>